import type { ReactNode } from "react";
import React, { useCallback, useMemo } from "react";

import { Link } from "@mui/material";
import clsx from "clsx";

import { Link as RouterLink } from "react-router-dom";

import styles from "./switcher.module.scss";

import Button, { ButtonSizes, ButtonTypes } from "../Button";

export enum SwitcherSizes {
	SM = "SM",
	MD = "MD"
}

export interface SwitcherOption {
	activeIcon?: ReactNode;
	icon?: ReactNode;
	label: string;
	value: string;
	link?: string;
}

interface SwitcherProps {
	value: string | any;
	onChange?: (val: string | any) => void;
	options: SwitcherOption[];
	optionWidth?: string | number;
	bordered?: boolean;
	activeLabel?: boolean;
	size?: SwitcherSizes;
	dark?: boolean;
	onlyIcons?: boolean;
	scrollableButtons?: boolean;
}

const Switcher: React.FC<SwitcherProps> = ({
	value,
	onChange,
	options,
	optionWidth,
	bordered,
	activeLabel,
	size = SwitcherSizes.MD,
	dark,
	onlyIcons,
	scrollableButtons
}) => {
	const activeOption = useMemo(() => options.find(x => x.value === value), [options, value]);

	const handleChange = useCallback(
		(val: string) => {
			if (onChange) {
				onChange(val);
			}
		},
		[onChange]
	);

	if (scrollableButtons) {
		return (
			<div className={styles.scrollableSwitcherWrapper}>
				{options.map(option => {
					const isActive = activeOption?.value === option?.value;
					if (option?.link) {
						return (
							<Link
								key={`opt-${option.value}`}
								to={option.link}
								component={RouterLink}
								className={clsx(styles.switcherOption, "no-underline", activeLabel && "text-yellow-350")}
							>
								<Button
									startIcon={option?.icon}
									variant={isActive ? ButtonTypes.GOLD : ButtonTypes.SIMPLE}
									size={ButtonSizes.SM}
									className={clsx("whitespace-nowrap px-0 w-fit")}
								>
									{!onlyIcons && (
										<p
											className={clsx(
												"font-mic px-6 text-base leading-4 text-center",
												isActive ? "text-brown-400" : "text-red-300"
											)}
										>
											{option.label}
										</p>
									)}
								</Button>
							</Link>
						);
					}

					return (
						<Button
							key={`opt-${option.value}`}
							startIcon={option?.icon}
							variant={isActive ? ButtonTypes.GOLD : ButtonTypes.SIMPLE}
							onClick={() => handleChange(option.value)}
							size={ButtonSizes.SM}
							className={clsx("whitespace-nowrap px-0 w-fit")}
						>
							{!onlyIcons && (
								<p
									className={clsx(
										"font-mic px-6 text-base leading-4 text-center",
										isActive ? "text-brown-400" : "text-red-300"
									)}
								>
									{option.label}
								</p>
							)}
						</Button>
					);
				})}
			</div>
		);
	}

	return (
		<div
			className={clsx(
				styles.switcherWrapper,
				bordered && styles.bordered,
				dark && styles.dark,
				size === SwitcherSizes.SM && styles.sizeSmall,
				size === SwitcherSizes.MD && styles.sizeMedium
			)}
		>
			<div className={styles.switcherContent}>
				{options.map(option => {
					if (option?.link) {
						return (
							<Link
								key={`opt-${option.value}`}
								to={option.link}
								component={RouterLink}
								className={clsx(styles.switcherOption, "no-underline", activeLabel && "text-yellow-350")}
								style={
									optionWidth
										? { width: typeof optionWidth === "string" ? optionWidth : `${optionWidth}px` }
										: { width: `${100 / options.length}%` }
								}
							>
								{option?.icon}
								{!onlyIcons && option.label}
							</Link>
						);
					}

					return (
						<div
							key={`opt-${option.value}`}
							className={styles.switcherOption}
							style={
								optionWidth
									? { width: typeof optionWidth === "string" ? optionWidth : `${optionWidth}px` }
									: { width: `${100 / options.length}%` }
							}
							onClick={() => handleChange(option.value)}
						>
							{option?.icon}
							{!onlyIcons && option.label}
						</div>
					);
				})}
				{!!activeOption && (
					<div
						className={styles.switcherActive}
						style={
							optionWidth
								? {
										width: typeof optionWidth === "string" ? optionWidth : `${optionWidth}px`,
										left: `calc(${(typeof optionWidth === "string" ? Number(optionWidth.replace(/\D/g, "")) : optionWidth) * (activeOption ? options.indexOf(activeOption) : 0)}% - ${!options.indexOf(activeOption) ? -2 : 2}px)`
									}
								: {
										width: `${100 / options.length}%`,
										left: `calc(${(100 / options.length) * (activeOption ? options.indexOf(activeOption) : 0)}% - ${!options.indexOf(activeOption) ? -2 : 2}px)`
									}
						}
					>
						{activeOption?.activeIcon || activeOption?.icon}
						{!onlyIcons && activeOption.label}
					</div>
				)}
			</div>
		</div>
	);
};
export default Switcher;
